"use client";

import { ButtonOverride as Button } from "@/components/Buttons/ButtonOverride";
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ChainId, type ChainIdNum, type ChainType, chainSettings, defaultNativeAddress, fallbackChainId } from "@/constants/chain-specific";
import { type ConnectButtonTheme, connectBtnTheme } from "@/constants/project-specific/theme/connectbutton";
import { usePortfolioOpen } from "@/hooks/modals/usePortfolioOpen";
import useBalances from "@/hooks/useBalances";
import { reverseResolve } from "@/lib/dns-names";
import { truncAddress } from "@/util/address";
import { cn } from "@/util/cn";
import { addressLink } from "@/util/explorerLinks";
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger, Link } from "@heroui/react";
import { useAppKit, useDisconnect as useAppkitDisconnect } from "@reown/appkit/react";
import { ArrowLeftRight, Network, Unplug, Wallet } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUnits } from "viem";
import { useConnection } from "wagmi";
import Col from "../Layout/Flex/Col";
import { QueryClient } from "@tanstack/react-query";
import { createIDBPersister } from "@/lib/persistor/idb";
import PortfolioDrawer from "../Portfolio/PortfolioDrawer";
import { useChewyBalanceOpen } from "@/hooks/modals/useChewyBalanceOpen";
import ChewyBalanceModal from "../Modals/ChewyBalanceModal";
import { AnimateNumber } from "motion-plus/react";
type ConnectButtonClasses = {
  button?: string;
  menu?: string;
};
type ConnectButtonOptions = {
  connectText?: string;
  connectingText?: string;
  networkButton?: boolean;
  customChainIcons?: ConnectButtonChainIconsType;
};
type ConnectButtonChainIconsType = [ChainType, string][];
interface ConnectButtonProps {
  classNames?: ConnectButtonClasses;
  className?: string;
  targetChainId?: ChainType;
  allowedChainIds?: ChainType | ChainType[];
  options?: ConnectButtonOptions;
}
export default function ConnectButton({
  allowedChainIds = [ChainId.SHIBARIUM],
  targetChainId,
  className,
  classNames,
  options
}: ConnectButtonProps) {
  const [wrongChain, setWrongChain] = useState(false);
  const {
    address,
    addresses,
    chainId,
    isConnected,
    isConnecting,
    isReconnecting,
    isDisconnected
  } = useConnection();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24 // 24 hours
      }
    }
  });

  // const persister = createIDBPersister("portfolio");

  const {
    open,
    close
  } = useAppKit();
  const theme: ConnectButtonTheme = connectBtnTheme;
  const defaultToken = chainId && theme.erc20BalanceToken && theme.erc20BalanceToken[chainId as ChainType] ? theme.erc20BalanceToken[chainId as ChainType] : undefined;
  const {
    balances
  } = useBalances(defaultToken?.address ? [defaultNativeAddress, defaultToken.address] : [defaultNativeAddress], address, undefined, chainId ?? fallbackChainId as ChainType);
  const defaultTokenLogo = useMemo(() => chainId && theme.erc20BalanceToken && theme.erc20BalanceToken[chainId as ChainType]?.logo, [chainId, theme.erc20BalanceToken]);
  const menuTheme = theme.dropdownTheme;
  const [isClient, setIsClient] = useState(false);
  const [accountName, setAccountName] = useState<string | undefined>(undefined);
  const {
    disconnect: appkitDisconnect
  } = useAppkitDisconnect();
  const {
    isOpen: isPortfolioOpen,
    onOpen: onPortfolioOpen,
    onOpenChange: onPortfolioOpenChange
  } = usePortfolioOpen();
  const [isOpen, setIsOpen] = useState(false);

  /*
  const PortfolioDrawer = dynamic(() => import("../Portfolio/PortfolioDrawer"), {
    ssr: false,
  });
  */

  useEffect(() => {
    // update some client side state to say it is now safe to render the client-side only component
    setIsClient(true);
  }, []);
  useEffect(() => {
    if (!isClient) setWrongChain(false);
    if (isConnected) {
      if (allowedChainIds && chainId) {
        if (Array.isArray(allowedChainIds) && !allowedChainIds.includes(chainId as ChainIdNum)) {
          setWrongChain(true);
        } else if (chainId !== allowedChainIds) {
          setWrongChain(true);
        } else {
          setWrongChain(false);
        }
      }
    }
  }, [chainId, allowedChainIds, isConnected]);
  useEffect(() => {
    const fetchAccountName = async () => {
      if (address) {
        const name = await reverseResolve(address, chainId as ChainIdNum);
        setAccountName(name);
      }
    };
    fetchAccountName();
  }, [address, chainId]);
  const sizeProp = useMemo(() => {
    if (isConnected) return theme.states?.connected.size ?? "md";
    if (isConnecting) return theme.states?.connecting.size ?? "md";
    return theme.states?.disconnected.size ?? "md";
  }, [isConnected, isConnecting, theme.states]);
  const radiusProp = useMemo(() => {
    if (isConnected) return theme.states?.connected.radius ?? "lg";
    if (isConnecting) return theme.states?.connecting.radius ?? "lg";
    return theme.states?.disconnected.radius ?? "lg";
  }, [isConnected, isConnecting, theme.states]);
  const variantProp = useMemo(() => {
    if (!isClient) return theme.states?.disconnected.variant ?? "shadow";
    if (isConnected) return theme.states?.connected.variant ?? "shadow";
    if (isConnecting) return theme.states?.connecting.variant ?? "shadow";
    return theme.states?.disconnected.variant ?? "shadow";
  }, [isClient, isConnected, isConnecting, theme.states]);
  const colorProp = useMemo(() => {
    if (!isClient) return theme.states?.disconnected.color ?? "success";
    if (isConnected) return theme.states?.connected.color ?? "success";
    if (isConnecting) return theme.states?.connecting.color ?? "warning";
    return theme.states?.disconnected.color ?? "success";
  }, [isClient, isConnected, isConnecting, theme.states]);
  const isLoadingProp = useMemo(() => {
    if (!isClient) return false;
    if (isConnected) return false;
    if (isConnecting) return true;
    return false;
  }, [isClient, isConnected, isConnecting]);
  const classNameProp = useMemo(() => {
    if (!isClient) return cn(theme.states?.disconnected.className ?? "", className ?? "");
    if (isConnected) return cn(theme.states?.connected.className ?? "", className ?? "");
    if (isConnecting) return cn(theme.states?.connecting.className ?? "", className ?? "");
    return cn(theme.states?.disconnected.className ?? "", className ?? "");
  }, [isClient, isConnected, isConnecting, theme.states, className]);
  const openModal = useCallback(() => {
    open();
  }, [open]);
  const handleDisconnect = useCallback(() => {
    appkitDisconnect();
  }, [appkitDisconnect]);
  const onClickProp = useMemo(() => {
    if (isConnected) return undefined;
    if (isConnecting) return handleDisconnect;
    return openModal;
  }, [isConnected, isConnecting, handleDisconnect, openModal]);
  const btnText = useMemo(() => {
    if (!isClient) return options?.connectText ?? "Connect";
    if (isConnected) return (address && accountName) ?? truncAddress(address ?? "");
    if (isConnecting) return options?.connectingText ?? theme.states?.connecting?.text ?? "Wait...";
    if (isReconnecting) return theme.states?.reconnecting?.text ?? "Reconnecting...";
    if (isDisconnected) return options?.connectText ?? theme.states?.disconnected?.text ?? "Connect";
    return options?.connectText ?? "Connect";
  }, [isClient, isConnected, isConnecting, isReconnecting, isDisconnected, options, theme.states, address, accountName]);
  const BalanceDisplay = () => {
    const {
      isOpen: isChewyBalanceOpen,
      onOpen: onChewyBalanceOpen,
      onOpenChange: onChewyBalanceOpenChange
    } = useChewyBalanceOpen();
    return <>
        <ChewyBalanceModal isOpen={isChewyBalanceOpen} onOpenChange={onChewyBalanceOpenChange} />
        <Col className="p-3">
          <div>
            <div className="flex flex-col w-full">


              <div className="flex row-end-auto justify-between text-sm font-semibold text-default-500">
                <div>Balance:</div>
                <span className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end text-right")}>
                  {chainSettings[chainId as ChainType]?.wNative.logo ? <img src={chainSettings[chainId as ChainType]?.wNative.logo} alt={chainSettings[chainId as ChainType]?.wNative.name} width="20px" height="20px" className="align-middle mr-1" /> : null}
                  <AnimateNumber format={{
                  notation: "standard"
                }} suffix={` ${chainId === 2000 ? "DOGE" : balances[defaultNativeAddress]?.symbol}`}>
                    {balances[defaultNativeAddress]?.balance?.bigInt ? ` ${Number(formatUnits(balances[defaultNativeAddress]?.balance?.bigInt, balances[defaultNativeAddress]?.decimals ?? 18)).toLocaleString()}` : ""}
                  </AnimateNumber>
                </span>
              </div>
              {defaultToken && Number(balances[defaultToken?.address]?.balance?.formatted ?? 0) > 0 && <p className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end text-right mt-1")}>
                  <Link onPress={() => {
                onChewyBalanceOpen();
                setIsOpen(false);
              }} className="cursor-pointer">
                    <span className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end font-bold text-right text-small")}>
                      {defaultTokenLogo ? <img src={defaultTokenLogo} alt="token logo" width="20px" height="20px" className="align-middle mr-1" /> : null}
                      <AnimateNumber format={{
                    notation: "standard"
                  }} suffix={` ${defaultToken && defaultToken.symbol}`}>
                        {Number(formatUnits(balances[defaultToken.address]?.balance?.bigInt, balances[defaultToken.address]?.decimals ?? 18)).toLocaleString()}
                      </AnimateNumber>
                    </span>
                  </Link>
                </p>}
            </div>
          </div>
        </Col>
      </>;
  };
  const iconClasses = "text-xl text-default-500 pointer-events-none flex-shrink-0";
  const {
    isOpen: isChewyBalanceOpen_0,
    onOpen: onChewyBalanceOpen_0,
    onOpenChange: onChewyBalanceOpenChange_0
  } = useChewyBalanceOpen();
  return <>
      {isConnected ? <>
          <ChewyBalanceModal isOpen={isChewyBalanceOpen_0} onOpenChange={onChewyBalanceOpenChange_0} />
          {address ? <PortfolioDrawer address={address} isOpen={isPortfolioOpen} onOpenChange={onPortfolioOpenChange} /> : null}
          <Dropdown className={menuTheme?.className} suppressHydrationWarning isOpen={isOpen} onOpenChange={setIsOpen}>
            <DropdownTrigger>
              <Button size={sizeProp} radius={radiusProp} variant={variantProp} color={!wrongChain ? "danger" : colorProp} isLoading={isLoadingProp} className={classNameProp} onPress={() => onClickProp}>
                {btnText}
              </Button>
            </DropdownTrigger>
            <DropdownMenu variant={menuTheme?.variant ?? "faded"} color={menuTheme?.color ?? "primary"} aria-label="Wallet Menu" topContent={isConnected && address && <>
                  <Col className="p-3">
                    <div>
                      <div className="flex flex-col w-full">


                        <div className="flex row-end-auto justify-between text-sm font-semibold text-default-500">
                          <div>Balance:</div>
                          <span className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end text-right")}>
                            {chainSettings[chainId as ChainType]?.wNative.logo ? <img src={chainSettings[chainId as ChainType]?.wNative.logo} alt={chainSettings[chainId as ChainType]?.wNative.name} width="20px" height="20px" className="align-middle mr-1" /> : null}
                            {balances[defaultNativeAddress]?.balance?.bigInt ? ` ${Number(formatUnits(balances[defaultNativeAddress]?.balance?.bigInt, balances[defaultNativeAddress]?.decimals ?? 18)).toLocaleString()} ${chainId === 2000 ? "DOGE" : balances[defaultNativeAddress]?.symbol}` : null}
                          </span>
                        </div>
                        {defaultToken && balances[defaultToken?.address]?.balance?.formatted > 0 && <p className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end text-right mt-1")}>
                            <Link onPress={() => {
                    onChewyBalanceOpen_0();
                    setIsOpen(false);
                  }} className="cursor-pointer">
                              <span className={cn(theme.balanceColor ?? "text-secondary", "inline-flex justify-end font-bold text-right text-small")}>
                                {defaultTokenLogo ? <img src={defaultTokenLogo} alt="token logo" width="20px" height="20px" className="align-middle mr-1" /> : null}
                                {Number(formatUnits(balances[defaultToken.address]?.balance?.bigInt, balances[defaultToken.address]?.decimals ?? 18)).toLocaleString()}{" "}
                                {defaultToken && defaultToken.symbol}
                              </span>
                            </Link>
                          </p>}
                      </div>
                    </div>
                  </Col>
                </>} classNames={menuTheme?.classNames} itemClasses={menuTheme?.itemClasses}>
              <DropdownSection showDivider>
                <DropdownItem key="network" onPress={() => open({
              view: "Networks"
            })} className="content-center" startContent={<Network className={iconClasses} />} textValue="Network Selection">
                  <w3m-network-button />
                </DropdownItem>
                <DropdownItem key="viewportfolio" shortcut="⌘P" description="View Portfolio" onPress={() => onPortfolioOpen('portfolio')} startContent={<Wallet className={iconClasses} />}>
                  Portfolio
                </DropdownItem>
                <DropdownItem key="viewwallet" shortcut="⌘W" description="View wallet information" onPress={() => open()} startContent={<Wallet className={iconClasses} />}>
                  Wallet
                </DropdownItem>
                <DropdownItem key="viewtxs" shortcut="⌘T" onPress={() => onPortfolioOpen('txs')} description="View Recent Transactions" startContent={<ArrowLeftRight className={iconClasses} />}>
                  Transactions
                </DropdownItem>
              </DropdownSection>
              <DropdownSection>
                <DropdownItem key="disconnectwallet" className="text-danger" color="danger" shortcut="⌘⇧D" description="Disconnect Wallet" onPress={handleDisconnect} startContent={<Unplug className={cn(iconClasses, "text-danger")} />}>
                  Disconnect
                </DropdownItem>
              </DropdownSection>
            </DropdownMenu>
          </Dropdown>
        </> : <Button size={sizeProp} radius={radiusProp} variant={variantProp} color={colorProp} isLoading={isLoadingProp} className={classNameProp} onPress={() => openModal()}>
          {btnText}
        </Button>}
    </>;
}